import React, { useState } from 'react'
import { Collapse, Spinner } from 'react-bootstrap'
import './MappingTester.scss'

interface TestResultDisplayProps {
  isLoading: boolean
  loadingText?: string
  hasResult: boolean
  isSuccess: boolean
  isError: boolean
  integrationName?: string
  successContent: React.ReactNode
  errorContent: React.ReactNode
  rawResult: unknown
  toggleShowLabel?: string
  toggleHideLabel?: string
  disableToggle?: boolean
}

export const TestResultDisplay: React.FC<TestResultDisplayProps> = ({
  isLoading,
  loadingText = 'Retrieving data',
  hasResult,
  isSuccess,
  isError,
  integrationName,
  successContent,
  errorContent,
  rawResult,
  toggleShowLabel = 'Show details',
  toggleHideLabel = 'Hide details',
  disableToggle = false,
}) => {
  const [showDetails, setShowDetails] = useState(false)

  if (isLoading) {
    return (
      <div className='bg-light rounded-2 py-5 mb-3 mt-3 d-flex flex-column align-items-center justify-content-center'>
        <Spinner animation='border' variant='secondary' className='mb-2' />
        <span className='zocks-text-gray-500 small'>{loadingText}</span>
      </div>
    )
  }

  if (!hasResult) {
    return null
  }

  return (
    <div className='bg-white border rounded-3 p-3 mt-3'>
      <p className='text-secondary text-uppercase fw-bold small mb-3 px-1'>TEST RESULT</p>

      {isSuccess && (
        <div className='zocks-bg-color-success-background zocks-border-0.5 p-4 mb-3 rounded-2'>
          <div className='d-flex align-items-center gap-2 mb-3'>
            <i className='bi bi-check-square-fill text-success'></i>
            <span className='fw-bold text-dark'>Test Completed Successfully</span>
          </div>
          {successContent}
        </div>
      )}

      {isError && (
        <div className='zocks-bg-color-danger-background zocks-border-0.5 p-4 mb-3 rounded-2'>
          <div className='d-flex align-items-center gap-2 mb-3'>
            <i className='bi bi-x-square-fill text-danger'></i>
            <span className='fw-bold text-dark'>{integrationName} Error</span>
          </div>
          {errorContent}
        </div>
      )}

      <div className={`rounded-3 overflow-hidden mt-3${showDetails ? ' zocks-bg-color-gray-900 text-white' : ''}`}>
        <div
          className={`d-flex justify-content-between align-items-center px-3 py-2 zocks-panel-toggle${disableToggle ? ' pe-none opacity-50' : ''}`}
          role='button'
          onClick={() => !disableToggle && setShowDetails((prev) => !prev)}
        >
          <div className='d-flex align-items-center gap-2 small'>
            <i className={showDetails ? 'bi bi-eye-slash' : 'bi bi-eye'}></i>
            <span className='fw-semibold'>{showDetails ? toggleHideLabel : toggleShowLabel}</span>
          </div>
          <i
            className='bi bi-copy small tester-copy-btn'
            role='button'
            onClick={(e) => {
              e.stopPropagation()
              navigator.clipboard.writeText(JSON.stringify(rawResult, null, 2))
            }}
          ></i>
        </div>
        <Collapse in={showDetails}>
          <div className='p-3 font-monospace small opacity-75 tester-details-body'>
            <pre className='mb-0 text-white'>{JSON.stringify(rawResult, null, 2)}</pre>
          </div>
        </Collapse>
      </div>
    </div>
  )
}
